import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function VisitNotesModal({ serverId, serviceUserId, onClose }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchNotes = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'visitNotes');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.visitDate || a.date || a.timestamp || a.createdAt);
                    const dateB = getMs(b.visitDate || b.date || b.timestamp || b.createdAt);
                    return dateB - dateA; // Newest first
                });
                
                setNotes(loaded);
            } catch (err) {
                console.error("Error fetching visit notes:", err);
                setError("Failed to load visit notes.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchNotes();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Visit Notes</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Carer logs from scheduled visits</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading visit notes...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : notes.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏡</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Visit Notes</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no carer logs recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {notes.map((note, index) => {
                                const carer = note.carer || note.author || note.createdBy || 'Unknown Carer';
                                const content = note.notes || note.details || note.summary || 'No notes provided';
                                const duration = note.duration || note.visitDuration || 'Unknown duration';
                                
                                return (
                                    <div key={note.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>Visit by {carer}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Duration: {duration}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#4b5563', fontWeight: 'bold' }}>
                                                {note.visitDate || note.date || note.timestamp || note.createdAt ? new Date((note.visitDate || note.date || note.timestamp || note.createdAt)?.seconds ? (note.visitDate || note.date || note.timestamp || note.createdAt).seconds * 1000 : (note.visitDate || note.date || note.timestamp || note.createdAt)).toLocaleString() : 'N/A'}
                                            </div>
                                        </div>
                                        
                                        <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '6px', fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                            {content}
                                        </div>
                                        
                                        {note.tasksCompleted && Array.isArray(note.tasksCompleted) && note.tasksCompleted.length > 0 && (
                                            <div style={{ marginTop: '16px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Tasks Completed</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {note.tasksCompleted.map((task, i) => (
                                                        <span key={i} style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', border: '1px solid #bbf7d0' }}>
                                                            ✓ {typeof task === 'string' ? task : task.name || 'Task'}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }
};
